import React, { useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Tag } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// CUSTOM HOOKS
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";

export default function ActionButtonPopover({
	label,
	toggleLabel,
	validStates,
	toggleValidStates,
	type,
	timer1,
	timer2,
	message = "",
	toggleMessage = "",
	color = "",
	toggleColor = "",
}: {
	type: string;
	timer1: number;
	timer2: number;
	label: string;
	toggleLabel: string;
	validStates: string[];
	toggleValidStates: string[];
	message?: string;
	toggleMessage?: string;
	color?: string;
	toggleColor?: string;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const [t] = useTranslation("global");

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */
	const { messages } = useGetAllMessages();

	// ************************************************ */
	// MEMOIZED VALUES ******************************** */
	// eslint-disable-next-line arrow-body-style
	const formatCamelCaseLabel = (value: string): string => {
		return value.replace(/([a-z])([A-Z])/g, "$1 $2");
	};

	const validStatesNames = useMemo(() =>
		validStates.map(id => {
			const msg = messages.find(m => m.id === id);
			return msg?.short_message ? formatCamelCaseLabel(msg.short_message) : id;
		})
		, [messages, validStates]);

	const toggleValidStatesNames = useMemo(() =>
		toggleValidStates.map(id => {
			const msg = messages.find(m => m.id === id);
			return msg?.short_message ? formatCamelCaseLabel(msg.short_message) : id;
		})
		, [messages, toggleValidStates]);

	// ************************************************ */
	// USE STATE VARIABLES **************************** */

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const getTitleStyles = () => {
		if (type === "TOGGLE") {
			return "generalStyles__info";
		}
		if (type === "TURNBACK") {
			return "generalStyles__warning";
		}
		if (type === "HOLDBACK") {
			return "generalStyles__success";
		}
		if (type === "TOGGLETIMER") {
			return "generalStyles__error";
		}
		return "";
	};

	// ************************************************ */
	// USE EFFECT ************************************* */

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div className="generalStyles__flex">
			<div>
				<div className="generalStyles__flex mt-1">
					<div className={getTitleStyles()} style={{ width: "100px" }}>
						{t("general.type")}:
					</div>
					{"  "}
					<div>
						<Tag color="blue">{type}</Tag>
					</div>
				</div>
				<div className="generalStyles__flex mt-1">
					<div className={getTitleStyles()} style={{ width: "100px" }}>
						{t("general.label")}:
					</div>
					{"  "}
					<div>{label}</div>
				</div>
				{message && (
					<div className="generalStyles__flex mt-1">
						<div className={getTitleStyles()} style={{ width: "100px" }}>
							{t("general.message")}:
						</div>
						{"  "}
						<div>
							<Tag color="purple">{message}</Tag>
						</div>
					</div>
				)}
				{color && (
					<div className="generalStyles__flex mt-1">
						<div className={getTitleStyles()} style={{ width: "100px" }}>
							{t("general.color")}:
						</div>
						{"  "}
						<div>
							<div style={{ width: "20px", height: "20px", backgroundColor: color, border: "1px solid #ccc", borderRadius: "4px" }} />
						</div>
					</div>
				)}
				{timer1 > 0 && (
					<div className="generalStyles__flex mt-1">
						<div className={getTitleStyles()} style={{ width: "100px" }}>
							{t("general.activeTime")}:
						</div>
						{"  "}
						<div>
							<Tag color="green">{timer1}s</Tag>
						</div>
					</div>
				)}
				<div className="generalStyles__flex mt-1">
					<div className={getTitleStyles()} style={{ width: "100px" }}>
						{t("general.validStates")}:
					</div>
					<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
						{validStatesNames.map((stateName: string, index: number) => (
							<Tag key={index} color="cyan">{stateName}</Tag>
						))}
					</div>
				</div>
			</div>
			{toggleValidStates && (
				<div
					className="generalStyles__mlFix25"
					style={{ borderLeft: "solid 1px #303030", paddingLeft: "25px" }}
				>
					<div className="generalStyles__flex mt-1">
						<div className={getTitleStyles()} style={{ width: "100px" }}>
							{t("general.label")}:
						</div>
						<div>{toggleLabel}</div>
					</div>
					{toggleMessage && (
						<div className="generalStyles__flex mt-1">
							<div className={getTitleStyles()} style={{ width: "100px" }}>
								{t("general.message")}:
							</div>
							{"  "}
							<div>
								<Tag color="purple">{toggleMessage}</Tag>
							</div>
						</div>
					)}
					{toggleColor && (
						<div className="generalStyles__flex mt-1">
							<div className={getTitleStyles()} style={{ width: "100px" }}>
								{t("general.color")}:
							</div>
							{"  "}
							<div>
								<div style={{ width: "20px", height: "20px", backgroundColor: toggleColor, border: "1px solid #ccc", borderRadius: "4px" }} />
							</div>
						</div>
					)}
					{timer2 > 0 && (
						<div className="generalStyles__flex mt-1">
							<div className={getTitleStyles()} style={{ width: "100px" }}>
								{t("general.activeTime")}:
							</div>
							{"  "}
							<div>
								<Tag color="green">{timer2}s</Tag>
							</div>
						</div>
					)}
					<div className="generalStyles__flex mt-1">
						<div className={getTitleStyles()} style={{ width: "100px" }}>
							{t("general.validStates")}:
						</div>
						<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
							{toggleValidStatesNames.map((stateName: string, index: number) => (
								<Tag key={index} color="cyan">{stateName}</Tag>
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
